import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { api, ApiError } from "../lib/api";
import { centsToDollars } from "@shared/pricing";
import type { PublicOrder } from "@shared/types";

type State =
  | { kind: "loading" }
  | { kind: "ready"; orders: PublicOrder[] }
  | { kind: "error"; message: string };

const statusLabel: Record<PublicOrder["status"], string> = {
  pending: "Pending",
  paid: "Paid",
  fulfilled: "Shipped",
  refunded: "Refunded",
  failed: "Failed",
};

export function OrdersPage() {
  const { user, userLoading, openSignInPanel } = useApp();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    if (userLoading) return;
    if (!user) {
      setState({ kind: "ready", orders: [] });
      return;
    }
    let cancelled = false;
    setState({ kind: "loading" });
    void (async () => {
      try {
        const res = await api.listOrders();
        if (!cancelled) setState({ kind: "ready", orders: res.orders });
      } catch (err) {
        if (!cancelled) {
          setState({
            kind: "error",
            message: err instanceof ApiError ? err.message : "Could not load orders",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, userLoading]);

  if (!userLoading && !user) {
    return (
      <main className="cart-page">
        <div className="wrap">
          <h1>
            Your <span className="it">orders.</span>
          </h1>
          <div className="cart-empty">
            <p>Sign in to see the books you've ordered.</p>
            <button className="btn btn-rust" onClick={openSignInPanel}>
              Sign in →
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="cart-page">
      <div className="wrap">
        <h1>
          Your <span className="it">orders.</span>
        </h1>

        {state.kind === "loading" && (
          <p className="serif-it" style={{ color: "var(--ink-soft)" }}>
            Loading…
          </p>
        )}

        {state.kind === "error" && (
          <div className="cart-empty">
            <p className="mono" style={{ color: "var(--rust)" }}>{state.message}</p>
          </div>
        )}

        {state.kind === "ready" && state.orders.length === 0 && (
          <div className="cart-empty">
            <p>No orders yet.</p>
            <button className="btn btn-rust" onClick={() => navigate("/")}>
              Browse the collection →
            </button>
          </div>
        )}

        {state.kind === "ready" && state.orders.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 24 }}>
            {state.orders.map((o) => (
              <button
                key={o.id}
                onClick={() => navigate(`/orders/${encodeURIComponent(o.orderNumber)}`)}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto auto auto",
                  alignItems: "center",
                  gap: 24,
                  textAlign: "left",
                  padding: "18px 22px",
                  background: "var(--paper)",
                  border: "1px solid var(--line)",
                  borderRadius: 4,
                  cursor: "pointer",
                  fontFamily: "var(--sans)",
                }}
              >
                <div>
                  <div
                    className="mono up"
                    style={{ fontSize: 11, color: "var(--ink-soft)", marginBottom: 4 }}
                  >
                    {new Date(o.createdAt).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                  <div className="serif" style={{ fontSize: 22, color: "var(--ink)" }}>
                    {o.orderNumber}
                  </div>
                  <div style={{ fontSize: 13, color: "var(--ink-soft)", marginTop: 4 }}>
                    {o.items.length} {o.items.length === 1 ? "book" : "books"}
                  </div>
                </div>
                <span
                  className="mono up"
                  style={{
                    fontSize: 11,
                    padding: "4px 10px",
                    border: "1px solid var(--line)",
                    borderRadius: 999,
                    color:
                      o.status === "paid" || o.status === "fulfilled"
                        ? "var(--forest)"
                        : o.status === "failed" || o.status === "refunded"
                          ? "var(--rust)"
                          : "var(--ink-soft)",
                  }}
                >
                  {statusLabel[o.status]}
                </span>
                <div className="serif-it" style={{ fontSize: 20, color: "var(--ink)" }}>
                  ${centsToDollars(o.totalCents)}
                </div>
                <span className="arr" style={{ color: "var(--ink-soft)" }}>
                  →
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
